/**
 * COB2B-785: order of the Saved List detail view.
 *
 * Rows are grouped under their Level 2 category heading. Groups follow their
 * Level 1 parent's position in the `order` menu (the mini cart's "View Ordered
 * Categories" order), then the Level 2 title alphabetically; rows within a
 * group sort by product title. Groups with no known Level 1 (the heading fell
 * back to the product's own category) come after those, alphabetically, and
 * the "Others" group (unresolved items) always comes last.
 *
 * Pure function — no DOM, no Preact — so it's covered by tests/groupListRows.test.mjs.
 */

const collator = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
});

const UNCATEGORISED = 'Uncategorised';

/**
 * `rows` — [{ title, category, categoryParent?, categoryParentIndex? }, ...]
 * `othersLabel` — the heading of the unresolved-items group.
 * Returns [{ key, category, rows }, ...] in display order.
 */
export function groupListRows(rows, othersLabel) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const category = row.category || UNCATEGORISED;
    const others = category === othersLabel;
    const parentIndex = others ? null : (row.categoryParentIndex ?? null);
    const parent = parentIndex == null ? '' : row.categoryParent || '';
    // The same Level 2 title could sit under two Level 1s; keep them apart.
    const key = `${parentIndex ?? ''}~~${parent}~~${category}`;
    if (!map.has(key)) {
      map.set(key, { key, category, parentIndex, others, rows: [] });
    }
    map.get(key).rows.push(row);
  });

  const rank = (g) => {
    if (g.others) return 2;
    return g.parentIndex == null ? 1 : 0;
  };

  return Array.from(map.values())
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.parentIndex ?? 0) - (b.parentIndex ?? 0) ||
        collator.compare(a.category, b.category),
    )
    .map(({ key, category, rows: groupRows }) => ({
      key,
      category,
      rows: [...groupRows].sort((a, b) =>
        collator.compare(a.title || '', b.title || ''),
      ),
    }));
}
